import { ReactElement } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { colorFillColorWithState, fontTypo } from 'mt-react-theme-manager';
import { Fonts } from 'mt-react-theme-manager/lib/typings';

import dataModel from '../../../models/data';
import localeModel from '../../../models/locale';
import { securityFaq } from '../../../configs/links.json';
import ContentSection from './ContentSection';
import EmailLabel from '../../../components/EmailLabel';
import TosAndPpLinks from '../../../components/TosAndPpLinks';
import type { MyAccountCoreConfig } from '../../../interfaces/ClientInfo';
import Scopes from './Scopes';
import { RequiredPolicyLinks } from '../../../components/RequiredPolicies/RequiredPolicyLinks';
import { OAuthConsentNotice } from '../../../components/OAuthConsentNotice';

import './styles.scss';

interface SSOOnboardFormContentProps {
  oauthConsentTosLink: MyAccountCoreConfig['oauthConsentTosLink'];
  oauthConsentPpLink: MyAccountCoreConfig['oauthConsentPpLink'];
  oauthConsentNotice: MyAccountCoreConfig['oauthConsentNotice'];
  email: string;
  clientId: string;
  clientName: string;
  showEmail?: boolean;
  remarksTypo?: Fonts;
  section1Title: string;
}

export default function SSOOnboardFormContent({
  email,
  clientName,
  oauthConsentNotice,
  oauthConsentTosLink,
  oauthConsentPpLink,
  section1Title,
  showEmail = true,
  remarksTypo = 'caption1'
}: SSOOnboardFormContentProps): ReactElement {
  const { t } = useTranslation();

  const {
    oAuth: { scope = '' }
  } = dataModel;
  const { language } = localeModel;

  const linkColor = colorFillColorWithState('primary');
  const notice =
    language === 'ja' ? oauthConsentNotice?.ja : oauthConsentNotice?.en;
  return (
    <div className={`sso-onboard-form-ja ${fontTypo('body1')}`}>
      <span className={`title ${fontTypo('h5')}`}>
        {t('SSOOnboardFormContent.title')}
      </span>
      <p className="description">
        <Trans
          i18nKey="SSOOnboardFormContent.description"
          tOptions={{ clientName }}
        >
          <span className={fontTypo('headline')} />
        </Trans>
      </p>

      {notice && <OAuthConsentNotice notice={notice} />}

      <div className="content-sections">
        <ContentSection
          headerNum={1}
          headerText={section1Title}
          className="content1"
        >
          {showEmail && (
            <>
              <p className="content1-description">
                {t('SSOOnboardFormContent.content1.description1', {
                  clientName
                })}
              </p>
              <EmailLabel email={email} />
            </>
          )}
          <div className={`remarks-section ${fontTypo(remarksTypo)}`}>
            <p className="remarks">
              <span className="dot">・</span>
              {t('SSOOnboardFormContent.content1.remark1', { clientName })}
            </p>
            <p className="remarks">
              <span className="dot">・</span>
              {t('SSOOnboardFormContent.content1.remark2', { clientName })}
            </p>
            <p className="remarks">
              <span className="dot">・</span>
              {t('SSOOnboardFormContent.content1.remark3', { clientName })}
            </p>
          </div>

          <p className="description2">
            <Trans
              i18nKey={'SSOOnboardFormContent.content1.description2'}
              components={{
                RequiredPolicyLinks: <RequiredPolicyLinks jaSeparator="、" />,
                SecurityFaqLink: (
                  <a
                    href={securityFaq.JP}
                    target="_blank"
                    className={linkColor}
                  />
                )
              }}
            />
          </p>
        </ContentSection>
        <ContentSection
          headerNum={2}
          headerText={t('SSOOnboardFormContent.content2.header', {
            clientName
          })}
          className="content2"
        >
          {scope && <Scopes scopes={scope.split(' ')} />}
          <div className={`duration-area ${fontTypo('caption1')}`}>
            <div>
              {t('SSOOnboardFormContent.content2.duration.description', {
                clientName
              })}
            </div>
            <div>
              {t('SSOOnboardFormContent.content2.duration.deletion', {
                clientName
              })}
            </div>
            <p>
              <Trans i18nKey="SSOOnboardFormContent.content2.duration.deletionImportant" />
            </p>
          </div>
        </ContentSection>
      </div>
      <p className="description2">
        <Trans
          i18nKey={'SSOOnboardFormContent.content2.description'}
          tOptions={{ clientName }}
          components={[
            <TosAndPpLinks
              tos={oauthConsentTosLink}
              pp={oauthConsentPpLink}
              linkClassName={colorFillColorWithState('primary')}
            />
          ]}
        />
      </p>
    </div>
  );
}
